import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Check, Star, Sparkles, Palette, ShieldCheck } from "lucide-react";

export default function Pricing() {
  const [plans, setPlans] = useState([]);
  const [busyPlan, setBusyPlan] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { api.get("/billing/plans").then((r) => setPlans(r.data)).catch(() => {}); }, []);

  // When the user hits a paid-plan CTA:
  //   - not logged in → send them to signup (they'll subscribe post-signup from Settings)
  //   - logged in → hit /paypal/subscribe and redirect to the returned approval URL.
  // Starter with trial available keeps the /signup path so newcomers can try before paying.
  const handleCta = async (plan) => {
    if (!user) { navigate("/signup"); return; }
    if (plan.trial_available && (user.plan === "trial" || !user.plan)) {
      // Trial user picking Starter — they're already on Starter trial, send to dashboard.
      navigate("/app/overview");
      return;
    }
    setBusyPlan(plan.id);
    try {
      const { data } = await api.post("/paypal/subscribe", { plan_id: plan.id });
      window.location.href = data.approval_url;
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not start PayPal checkout.";
      toast.error(msg);
      setBusyPlan(null);
    }
  };

  return (
    <>
      <SEO pageKey="pricing" title="Pricing — SerpNex" description="Simple pricing for agencies, freelancers, and growing brands. Every plan includes every feature — just pick the keyword volume that fits. 7-day free trial on Starter." canonical="/pricing" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-12 text-center">
        <div className="eyebrow">Pricing</div>
        <h1 className="mt-3 h-display text-4xl sm:text-5xl md:text-6xl font-bold text-[#0A0D14] max-w-4xl mx-auto leading-tight">
          Every feature. Every plan. Just pick the keyword volume.
        </h1>
        <p className="mt-5 text-[17px] text-[#525B6A] max-w-2xl mx-auto">
          White-label reports, team seats, activity log, ranking alerts — included on every plan. Start with a 7-day free trial on Starter, upgrade anytime as you grow.
        </p>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        {/* Intro promo banner */}
        <div className="mb-8 mx-auto max-w-3xl text-center" data-testid="intro-promo-banner">
          <div className="inline-flex items-center gap-2 pill pill-accent text-[13px]">
            <Sparkles className="h-3.5 w-3.5" /> Limited-time promo · Discounted first month on every plan
          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans.map((p) => {
            const kwCount = p.keywords;
            const hasIntro = typeof p.intro_price_monthly === "number" && p.intro_price_monthly < p.price_monthly;
            return (
              <div key={p.id} data-testid={`plan-${p.id}`} className={`card-crisp p-7 relative flex flex-col ${p.popular ? 'ring-2 ring-[#0047FF] shadow-[0_10px_30px_rgba(0,71,255,0.14)]' : ''}`}>
                {p.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 pill pill-accent">
                    <Star className="h-3 w-3" /> Most popular
                  </span>
                )}
                <div className="text-[13px] uppercase tracking-widest text-[#8A94A6] font-medium">{p.name}</div>

                <div className="mt-2 text-xs text-[#525B6A]">{kwCount} keywords</div>

                <div className="mt-4 flex items-baseline gap-2">
                  <span className="stat-value text-4xl font-bold text-[#0047FF]" data-testid={`plan-price-${p.id}`}>${hasIntro ? p.intro_price_monthly : p.price_monthly}</span>
                  {hasIntro && (
                    <span className="text-[#8A94A6] text-lg line-through tabular-nums" data-testid={`plan-price-strike-${p.id}`}>${p.price_monthly}</span>
                  )}
                  <span className="text-[#525B6A] text-sm">/ mo</span>
                </div>
                {hasIntro ? (
                  <div className="mt-1 text-xs text-[#0047FF] font-medium" data-testid={`plan-intro-note-${p.id}`}>{p.intro_note}</div>
                ) : (
                  <div className="mt-1 text-xs text-[#525B6A]">Billed monthly · cancel anytime</div>
                )}

                {p.trial_available ? (
                  <div className="mt-3 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-emerald-700 font-semibold" data-testid={`plan-trial-badge-${p.id}`}>
                    <ShieldCheck className="h-3 w-3" /> {p.trial_days}-day free trial available
                  </div>
                ) : (
                  <div className="mt-3 text-[11px] uppercase tracking-wider text-[#8A94A6] font-medium">No trial · direct start</div>
                )}

                <button
                  onClick={() => handleCta(p)}
                  disabled={busyPlan === p.id}
                  data-testid={`plan-cta-${p.id}`}
                  className={`mt-5 block text-center w-full px-4 py-2.5 rounded-md text-sm font-semibold ${p.popular ? 'btn-primary' : 'btn-ghost'} disabled:opacity-60`}
                >
                  {busyPlan === p.id ? "Redirecting to PayPal…" : (p.cta || (p.trial_available ? "Start 7-Day Free Trial" : `Get ${p.name}`))}
                </button>

                {p.trial_available && (
                  <p className="mt-2 text-[11px] text-[#8A94A6] leading-relaxed" data-testid={`plan-trial-detail-${p.id}`}>Trial limits: {p.trial_keywords} keywords · {p.trial_projects} project. Full quota unlocks after upgrade.</p>
                )}

                <ul className="mt-5 space-y-2 border-t border-[#E2E8F0] pt-5">
                  {p.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2 text-[13px] text-[#0A0D14]">
                      <Check className="h-3.5 w-3.5 text-emerald-600 mt-0.5 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* All plans include everything */}
        <div className="mt-10 text-center text-sm text-[#525B6A] max-w-3xl mx-auto" data-testid="all-plans-note">
          Every plan includes the full feature set — the only thing that changes is how many keywords you can track. Websites, team seats, white-label reports, and everything else are always unlimited.
        </div>

        {/* Trial-only-on-starter note */}
        <div className="mt-16 card-crisp p-8 md:p-10 text-center max-w-3xl mx-auto" data-testid="paid-trial-note">
          <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-[#0047FF] font-semibold">
            <Sparkles className="h-3.5 w-3.5" /> New to SerpNex?
          </div>
          <p className="mt-3 text-[17px] text-[#525B6A] leading-relaxed">
            Try SerpNex free for 7 days on the Starter plan — 5 keywords, 1 project, no credit card required. Upgrade to a full plan anytime to unlock your complete keyword quota.
          </p>
          <Link
            to="/signup"
            data-testid="paid-trial-cta"
            className="mt-6 inline-flex btn-primary px-6 py-3 rounded-md text-sm items-center gap-2"
          >
            Start 7-Day Free Trial
          </Link>
        </div>

        <p className="mt-6 text-center text-xs text-[#8A94A6] max-w-2xl mx-auto" data-testid="promo-fineprint">
          Promo pricing applies to your first month only. From the second month onward you're billed at the regular monthly rate shown alongside each plan.
        </p>

        {/* Short pricing support text */}
        <p className="mt-14 text-center text-[15px] text-[#525B6A] max-w-3xl mx-auto leading-relaxed" data-testid="pricing-support-text">
          Whether you manage client campaigns, your own business website, or content projects, SerpNex gives you a simple way to track keyword rankings with clarity.
        </p>

        {/* White-label reports mention */}
        <div className="mt-14 card-crisp p-8 md:p-10 grid md:grid-cols-12 gap-8 items-center max-w-5xl mx-auto" data-testid="white-label-mention">
          <div className="md:col-span-2 flex md:justify-center">
            <div className="h-14 w-14 rounded-xl bg-[#E0E7FF] text-[#0047FF] flex items-center justify-center">
              <Palette className="h-6 w-6" />
            </div>
          </div>
          <div className="md:col-span-10">
            <div className="text-xs uppercase tracking-widest text-[#0047FF] font-semibold">Premium feature</div>
            <div className="mt-2 text-2xl font-display font-bold text-[#0A0D14]">Need to share rankings professionally?</div>
            <p className="mt-2 text-[15px] text-[#525B6A] leading-relaxed">
              White-label reports make it easy to present keyword performance to clients and stakeholders.
            </p>
          </div>
        </div>

        {/* Small trust note */}
        <div className="mt-14 flex items-center justify-center gap-2 text-xs text-[#8A94A6] max-w-2xl mx-auto text-center" data-testid="trust-note">
          <ShieldCheck className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
          <span>No bloated SEO suite. Just clear, reliable keyword tracking with flexible plans for solo users and teams.</span>
        </div>

        <p className="text-center text-xs text-[#8A94A6] mt-8">
          Billing via PayPal. Prices in USD.
        </p>
      </div>
    </>
  );
}
